import theme from '@frete.com/fuel-foundation/fretebras/theme.json';
import { FuelCallout, FuelIcon, FuelToast, FuelTooltip, FuelTypography } from '@frete.com/fuel-react';
import { FuelFlex } from '@frete.com/fuel-station-react';
import { FormModalUpsell } from 'components/Externals/FormModalUpsell';
import FiltersDrawer from 'components/FiltersDrawer';
import ModalBlockToUpSell from 'components/ModalBlockToUpSell';
import ModalConfirmShareFreights from 'components/ModalConfirmShareFreights';
import ModalPurchaseExtraSuggestedTruckers from 'components/ModalPurchaseExtraSuggestedTruckers';
import SearchTrucker from 'components/SearchTrucker';
import ShareFreight from 'components/ShareFreight';
import Header from 'components/TruckerList/Sidebar/Header';
import { BREAKPOINTS, dsTheme } from 'constants/styled.contants';
import AppContext from 'context';
import { SUGGESTED_TAB, TARIFF_URL } from 'context/reducers/tabs.type';
import { useAmplitude } from 'hooks/useAmplitude';
import { useAppStates } from 'hooks/useAppStates';
import { useFilteredData } from 'hooks/useFilteredData';
import { useGlobals } from 'hooks/useGlobals';
import { useLoadTruckers } from 'hooks/useLoadTruckers';
import { usePayPerUseExtraTruckers } from 'hooks/usePayPerUseExtraTruckers';
import { useSelectAllTruckers } from 'hooks/useSelectAllTruckers';
import { useShareFreightWithTruckers } from 'hooks/useShareFreightWithTruckers';
import { useTracking } from 'hooks/useTracking';
import { useUserCombo } from 'hooks/useUserCombo';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { transformDataToTracking } from 'utils/transformDataToTracking';
import { DrawerCheckout } from 'components/Externals/DrawerCheckout';
import BackToTopButton from './BackToTopButton';
import CheckboxOptions from './CheckboxOptions';
import { Listing } from './Listing';
import SearchedTrucker from './SearchedTrucker';
import * as Styled from './styled';
import TotalTruckers from './TotalTruckers';
import UnlockTruckersButton from './UnlockTruckersButton';
import ConsumptionLabel from './UnlockTruckersButton/ConsumptionLabel';

const Sidebar: FC<{
  animation?: 'none' | 'left' | 'right';
}> = ({ animation }) => {
  const {
    state: {
      truckers: { radius },
      searchTruckers: { isSearchFieldOnFocus, term },
      suggestedTruckers: { isFetchingSuggested },
      tabs: { activeTab },
      shareFreights: { truckers, isOpenModalShareFreight },
    },
  } = useContext(AppContext);
  const { user } = useGlobals();
  const { selectAllTruckers, unselectAllTruckers } = useSelectAllTruckers();
  const { toggleModalShareFreight, handleShareFreightWithTruckers } = useShareFreightWithTruckers();
  const { isLoadErrorFull } = useAppStates();
  const { filteredDataSuggested } = useFilteredData();
  const { fetchTruckers } = useLoadTruckers('lazy');
  const { sendAmplitude } = useAmplitude();
  const { sendTracking } = useTracking();
  const { showPurchaseExtraTruckersButton } = usePayPerUseExtraTruckers();
  const { freightId, activeTab: urlPath } = useParams();
  const { hasBannerUpgrade } = useUserCombo();
  const toastWantAccessRef = useRef(null);
  const sidebarRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [toUpdateRequestSuggestedWithTimes, setToUpdateRequesSuggestedWithTimes] = useState('');
  const [showBackToTopButton, setShowBackToTopButton] = useState(false);
  const [backToTopButtonPositionLeft, setBackToTopButtonPositionLeft] = useState(0);

  const { planMigrationSource } = useGlobals();

  const isChatSource = planMigrationSource?.isChatFocusSource || planMigrationSource?.isChatGeneralSource;

  const showMobileTotalDisplay = window.innerWidth <= BREAKPOINTS.max.sm;

  const getLastUpdateFilter = () =>
    `Atualizado às ${new Date().toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;

  const [isOpenFormModal, setIsOpenFormModal] = useState(false);
  const toggleModal = () => setIsOpen(!isOpen);
  const handleFormModal = () => {
    sendAmplitude('menu_clicked', {
      companyId: user?.data.company.id,
      userId: user?.data.id,
      combo: user?.data.company.combo.name,
      type: 'share_freight_change_plan_ask_for_contact',
    });
    setIsOpenFormModal(false);
  };

  const handleReloadSuggested = () => {
    unselectAllTruckers();
    setToUpdateRequesSuggestedWithTimes(getLastUpdateFilter);

    if (isFetchingSuggested) return;

    sendAmplitude('trucker_listing_reload_page', {
      freightCode: freightId,
      drive: filteredDataSuggested,
    });
    sendTracking({
      event: {
        name: 'trucker_listing_reload_page',
      },
      data: {
        freightId: `hashed|${freightId}`,
        drive: transformDataToTracking(filteredDataSuggested),
      },
    });
    fetchTruckers(radius);
  };

  const handleShareFreightAutomatic = () => {
    if (hasBannerUpgrade) {
      toggleModal();
    }
    selectAllTruckers();
  };

  const handleBackToTop = () => {
    if (sidebarRef?.current) {
      sidebarRef?.current?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleScroll = () => {
    setShowBackToTopButton(sidebarRef?.current?.scrollTop > 100);
  };

  useEffect(() => {
    setToUpdateRequesSuggestedWithTimes(getLastUpdateFilter);
  }, []);

  useEffect(() => {
    if (!sidebarRef?.current) return undefined;

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setBackToTopButtonPositionLeft(entry.contentRect.width);
      });
    });
    observer.observe(sidebarRef.current);

    return () => {
      observer.disconnect();
    };
  }, [sidebarRef?.current]);

  return (
    <Styled.Sidebar animation={animation} ref={sidebarRef} onScroll={handleScroll}>
      {!!!isLoadErrorFull && (
        <>
          <Styled.HeaderWrapper>
            <Header />

            <SearchTrucker />
          </Styled.HeaderWrapper>

          {truckers.length > 0 && (
            <Styled.CheckboxOptionsWrapper>
              <CheckboxOptions />
            </Styled.CheckboxOptionsWrapper>
          )}
          {!showMobileTotalDisplay && showPurchaseExtraTruckersButton && (
            <FuelFlex
              defaultProps={{
                width: '100%',
                justifyContent: 'flex-end',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 'km2',
                customPadding: `${dsTheme.spacing.km6} 0 0 0`,
              }}
            >
              <UnlockTruckersButton />
              <ConsumptionLabel />
            </FuelFlex>
          )}
          {truckers.length === 0 && (
            <Styled.FiltersWrapper $showPurchaseExtraTruckersButton>
              <FuelFlex
                defaultProps={{ width: '100%', alignItems: 'center' }}
                smProps={{ flexDirection: 'column', gap: 'km2', alignItems: 'flex-end' }}
              >
                <FuelFlex
                  defaultProps={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}
                  smProps={{ gap: 'km2', flexWrap: 'wrap' }}
                >
                  {!isSearchFieldOnFocus && (
                    <>
                      <FuelFlex
                        defaultProps={{ gap: 'km2' }}
                        smProps={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}
                      >
                        {!showMobileTotalDisplay && (
                          <>
                            <FiltersDrawer />
                            <FuelTooltip
                              content="Envie o frete diretamente para o app dos motoristas sugeridos."
                              placement="bottom-start"
                            >
                              <FuelFlex defaultProps={{ minHeight: 'fit-content' }}>
                                <ShareFreight handleClick={handleShareFreightAutomatic} />
                              </FuelFlex>
                            </FuelTooltip>
                          </>
                        )}
                        {showMobileTotalDisplay && (
                          <>
                            <FuelFlex
                              defaultProps={{
                                width: '100%',
                                gap: 'km4',
                              }}
                              smProps={{ width: 'auto' }}
                            >
                              <FiltersDrawer />
                              <FuelTooltip
                                content="Envie o frete diretamente para o app dos motoristas sugeridos."
                                placement="bottom-start"
                              >
                                <FuelFlex defaultProps={{ minHeight: 'fit-content' }}>
                                  <ShareFreight handleClick={handleShareFreightAutomatic} />
                                </FuelFlex>
                              </FuelTooltip>
                            </FuelFlex>
                            <TotalTruckers />
                            {showPurchaseExtraTruckersButton && (
                              <FuelFlex
                                defaultProps={{
                                  flexDirection: 'column',
                                  width: '100%',
                                  customPadding: `${dsTheme.spacing.km5} 0`,
                                }}
                              >
                                <Styled.ExtraButtonsWrapper>
                                  <UnlockTruckersButton />
                                  <ConsumptionLabel />
                                </Styled.ExtraButtonsWrapper>
                              </FuelFlex>
                            )}
                          </>
                        )}
                      </FuelFlex>

                      {!isFetchingSuggested && (
                        <Styled.ContentResultAndUpdate
                          isChatSource={isChatSource}
                          hasTarrifTab={urlPath === TARIFF_URL}
                          $showPurchaseExtraTruckersButton={showPurchaseExtraTruckersButton}
                        >
                          <span>
                            <Styled.UpdateWrapper $showPurchaseExtraTruckersButton={showPurchaseExtraTruckersButton}>
                              {activeTab === SUGGESTED_TAB && (
                                <>
                                  <FuelTypography weight="medium" size="caption" color="neutral-default">
                                    {toUpdateRequestSuggestedWithTimes}
                                  </FuelTypography>

                                  <FuelTooltip label="Atualizar sugeridos Fretebras" placement="bottom-end">
                                    <FuelIcon
                                      name="reload"
                                      onClick={handleReloadSuggested}
                                      data-testid="reload-button-desktop-suggested"
                                      style={{ color: theme.color.icon.neutral.default }}
                                    />
                                  </FuelTooltip>
                                </>
                              )}
                            </Styled.UpdateWrapper>
                            {!showMobileTotalDisplay && <TotalTruckers />}
                          </span>
                        </Styled.ContentResultAndUpdate>
                      )}
                    </>
                  )}
                </FuelFlex>
              </FuelFlex>
            </Styled.FiltersWrapper>
          )}
          {isChatSource && (
            <FuelCallout
              color="green"
              icon="user-area"
              heading="Com o Chat Fretebras você tem mais contatos liberados"
              message="Agora você acessa até 20 motoristas sugeridos"
              iconShape
              iconStyle="filled"
              fullWidth
              style={{ marginTop: '15px' }}
            />
          )}
          {!term?.length && !isSearchFieldOnFocus && <Listing truckerListData={filteredDataSuggested} />}

          {isSearchFieldOnFocus && <SearchedTrucker />}

          <Styled.WrapperToast>
            <FuelToast ref={toastWantAccessRef} severity="success" heading="Entraremos em contato com você." />
          </Styled.WrapperToast>

          <ModalConfirmShareFreights
            isOpen={isOpenModalShareFreight}
            handleOnClose={toggleModalShareFreight}
            retryShareFreights={handleShareFreightWithTruckers}
          />
          <BackToTopButton
            showBackToTopButton={showBackToTopButton}
            handleBackToTop={handleBackToTop}
            left={backToTopButtonPositionLeft}
          />
        </>
      )}
      <ModalBlockToUpSell toggleModal={toggleModal} isOpen={isOpen} handleFormModal={() => setIsOpenFormModal(true)} />
      <FormModalUpsell
        isOpen={isOpenFormModal}
        onClose={() => setIsOpenFormModal(false)}
        handleFormModal={handleFormModal}
        toggleFormModal={() => setIsOpenFormModal((prev) => !prev)}
        feature="compartilhamentofretemotoristassugeridos"
      />
      <ModalPurchaseExtraSuggestedTruckers />
      <DrawerCheckout />
    </Styled.Sidebar>
  );
};

export default Sidebar;
